/**
 * React hook for managing user profiles
 */
import { useState, useEffect, useCallback } from 'react';
import { Identity, DAGStore, DAGObject, ProfileContent, createProfile } from '@amorphous/core';

interface UseProfileResult {
  profile: DAGObject<ProfileContent> | null;
  loading: boolean;
  error: Error | null;
  hasProfile: boolean;
  profileCache: Map<string, DAGObject<ProfileContent>>;
  saveProfile: (options: { displayName: string; bio?: string; avatarDataUrl?: string }) => Promise<DAGObject<ProfileContent>>;
  getDisplayName: (pubkey: string) => string;
  getProfileByPubkey: (pubkey: string) => DAGObject<ProfileContent> | null;
  refreshCache: () => Promise<void>;
}

export function useProfile(
  identity: Identity | null,
  store: DAGStore | null,
  broadcastObject?: (obj: DAGObject<ProfileContent>) => void
): UseProfileResult {
  const [profile, setProfile] = useState<DAGObject<ProfileContent> | null>(null);
  const [profileCache, setProfileCache] = useState<Map<string, DAGObject<ProfileContent>>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Build profile cache from all profiles in store
  const refreshCache = useCallback(async () => {
    if (!store) return;
    try {
      const profiles = await store.getAllProfiles();
      const cache = new Map<string, DAGObject<ProfileContent>>();
      for (const p of profiles) {
        const existing = cache.get(p.creator);
        // Keep the most recent profile for each creator
        if (!existing || p.timestamp > existing.timestamp) {
          cache.set(p.creator, p);
        }
      }
      setProfileCache(cache);
    } catch (err) {
      console.warn('[Profile] Failed to build cache:', err);
    }
  }, [store]);

  // Load own profile and build cache on mount
  useEffect(() => {
    async function loadProfile() {
      if (!identity || !store) {
        setLoading(false);
        return;
      }
      try {
        // Load own profile
        const myProfile = await store.getProfileByCreator(identity.publicKey);
        setProfile(myProfile);

        // Build cache
        await refreshCache();
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load profile'));
      } finally {
        setLoading(false);
      }
    }
    loadProfile();
  }, [identity, store, refreshCache]);

  // Save or update profile
  const saveProfile = useCallback(async (options: {
    displayName: string;
    bio?: string;
    avatarDataUrl?: string;
  }) => {
    if (!identity || !store) {
      throw new Error('No identity or store');
    }

    const profileObj = createProfile(identity, options);
    console.log('[Profile] Saving profile:', {
      hash: profileObj.hash,
      creator: profileObj.creator?.slice(0, 8),
      timestamp: profileObj.timestamp,
      signature: profileObj.signature?.slice(0, 16),
    });

    try {
      await store.put(profileObj);
    } catch (err) {
      console.error('[Profile] Store.put failed:', err);
      throw err;
    }

    setProfile(profileObj);

    // Update cache with new profile
    setProfileCache(prev => {
      const next = new Map(prev);
      next.set(identity.publicKey, profileObj);
      return next;
    });

    // Broadcast to peers
    if (broadcastObject) {
      broadcastObject(profileObj);
    }

    return profileObj;
  }, [identity, store, broadcastObject]);

  // Get display name for any public key (uses cache, falls back to truncated key)
  const getDisplayName = useCallback((pubkey: string | undefined): string => {
    if (!pubkey) return 'Unknown';
    const cachedProfile = profileCache.get(pubkey);
    if (cachedProfile?.content?.displayName) {
      return cachedProfile.content.displayName;
    }
    // Fallback to truncated public key
    return `${pubkey.slice(0, 6)}...${pubkey.slice(-4)}`;
  }, [profileCache]);

  // Get full profile object for a pubkey
  const getProfileByPubkey = useCallback((pubkey: string): DAGObject<ProfileContent> | null => {
    return profileCache.get(pubkey) ?? null;
  }, [profileCache]);

  return {
    profile,
    loading,
    error,
    hasProfile: profile !== null,
    profileCache,
    saveProfile,
    getDisplayName,
    getProfileByPubkey,
    refreshCache,
  };
}
